// Psychologist/PsychologistDetail.tsx
import React, {useContext, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {IConsultationConfig} from '../../Types/psychology/consultation';
import {useTheme} from 'Theme/ThemeContext';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useMediaQuery} from '@mui/material';
import {AuthContext, AuthContextType} from 'Auth/AuthContext';
import {Message} from 'Core/components/Message';
import {axios} from 'Auth/axiosConfig';
import pprint from '../../Utils/pprint';

import {calculateAge, calculateExperienceYears, getYearWord, removeSettingsParamsFromURL} from './utils';

// UI Components
import CircularProgress from 'Core/components/elements/CircularProgress';
import GuaranteeCard from 'Landing/GuaranteeCard';
import PsychologistSpecializations from 'Psychologist/PsychologistSpecializations';
import PsychologistAboutTabs from 'Psychologist/PsychologistAboutTabs';
import PsychologistAvatar from 'Psychologist/PsychologistAvatar';
import CommonOrderOptionsPicker from '../ConsultationPackage/CommonOrderOptionsPicker';
import FavoriteButton from 'Psychologist/Favorites/FavoriteButton';
import ConfirmationModal from 'Psychologist/ConfirmationModal';
import Button from 'Core/components/elements/Button/Button';
import {FC, FCEC, FCSC, FR, FRC} from 'WideLayout/Layouts';
import {usePsychologist} from "Psychologist/usePsychologist";
import {useReservationPaid} from "Psychologist/useReservationPaid";
import PsychologistHead from "Psychologist/PsychologistHead";

interface FormValues {
    consultation_config: IConsultationConfig | null;
    selected_slot: string | null;
}

interface PsychologistDetailProps {
    className?: string;
    id?: number;
    psychologist?: any;
    headMeta?: boolean;
    style?: any;
    containerRef?: React.RefObject<HTMLDivElement>;
}

const PsychologistDetail: React.FC<PsychologistDetailProps> = (
    {
        className,
        id: propId = undefined,
        psychologist: propPsychologist = undefined,
        style,
        containerRef,
    }) => {
    const {plt, theme} = useTheme();
    const {id: urlId} = useParams<{ id: string }>();
    const {user} = useContext(AuthContext) as AuthContextType;
    const finalId = propId || urlId;
    const {psychologist, loading, notFound} = usePsychologist(finalId, propPsychologist);
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const reservationPaid = queryParams.get('reservation_paid');
    const next = queryParams.get('next') || '/order/package';
    const configParam = queryParams.get('config');
    const [defaultConsultationConfigId, setDefaultConsultationConfigId] = useState<number | undefined>(
        configParam ? parseInt(configParam, 10) : undefined
    );
    const [defaultDateTimeSlot, setDefaultDateTimeSlot] = useState<string | undefined>(undefined);
    const {control, handleSubmit, setValue, watch, formState: {isValid}} = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: {
            consultation_config: null,
            selected_slot: null,
        },
    });
    const selectedSlotWatch = watch('selected_slot');

    // Hooks
    useReservationPaid(reservationPaid, setValue, setDefaultConsultationConfigId, setDefaultDateTimeSlot);

    const [showModal, setShowModal] = useState(false);
    const signUpRef = useRef<HTMLDivElement | null>(null);
    const [isButtonVisible, setIsButtonVisible] = useState<boolean>(true);
    const internalContainerRef = useRef<HTMLDivElement | null>(null);
    const effectiveContainerRef = containerRef || internalContainerRef;
    const isGt600 = useMediaQuery('(min-width: 600px)');

    // Manage Scroll Visibility
    useEffect(() => {
        if (!effectiveContainerRef?.current) return;
        const container = effectiveContainerRef.current;

        const onScroll = () => {
            const {scrollHeight, scrollTop, clientHeight} = container;
            const remainingScroll = scrollHeight - scrollTop - clientHeight;
            setIsButtonVisible(remainingScroll > 200);
        };

        container.addEventListener('scroll', onScroll);
        onScroll();
        setTimeout(() => {
            onScroll();
        }, 150)
        return () => {
            container.removeEventListener('scroll', onScroll);
        };
    }, [effectiveContainerRef]);

    const scrollToSignUp = () => {
        if (effectiveContainerRef?.current && signUpRef.current) {
            const container = effectiveContainerRef.current;
            const signUpPosition = signUpRef.current.offsetTop;
            container.scrollTo({
                top: signUpPosition,
                behavior: 'smooth',
            });
        }
    };

    const appendReservationPaid = (url: string): string => {
        if (!reservationPaid) return url;
        const urlObj = new URL(url, window.location.origin);
        urlObj.searchParams.set('reservation_paid', reservationPaid);
        return urlObj.pathname + urlObj.search;
    };

    // Submit handlers
    const proceedToOrder = (data: FormValues) => {
        const existingData = JSON.parse(localStorage.getItem('clientRequest') || '{}');
        const updatedData: any = {...existingData};

        updatedData.consultation_config = data.consultation_config;
        updatedData.selected_slot = data.selected_slot;
        updatedData.psychologist = psychologist;

        if (reservationPaid) {
            updatedData.reservation_paid = parseInt(reservationPaid, 10);
        }

        localStorage.setItem('clientRequest', JSON.stringify(updatedData));

        const targetUrl = appendReservationPaid(next);
        navigate(targetUrl);
    };

    const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
        pprint('Pre order submitted: ', data);
        if (!data.consultation_config || !data.selected_slot) return;

        try {
            const response = await axios.get('/api/v1/consultations/psy-config-datetime/get-can-paid-by-balance/', {
                params: {
                    psychologist_id: psychologist?.id,
                    config_id: data.consultation_config.id,
                    date: data.selected_slot,
                },
            });
            pprint('response', response);
            if (response.status === 200 && response.data) {
                setShowModal(true);
            }
        } catch (error) {
            pprint('Не оплатить с баланса:', error);
            proceedToOrder(data);
        }
    };

    const handleUseBalance = () => {
        axios.post('/api/v1/consultations/psy-config-datetime/pay-by-balance/', {
            psychologist_id: psychologist?.id,
            config_id: watch('consultation_config')?.id,
            date: watch('selected_slot'),
        }).then(response => {
            const consultationId = response.data.id;
            const targetUrl = appendReservationPaid(`/consultations/${consultationId}`);
            Message.success('Оплата с баланса успешно проведена');
            navigate(targetUrl);
        }).catch(e => {
            pprint('Ошибка при оплате с баланса', e);
        });
    };

    const handleProceedToPayment = () => {
        proceedToOrder({
            consultation_config: watch('consultation_config'),
            selected_slot: watch('selected_slot'),
        });
    };

    const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

    if (loading || !psychologist) return <CircularProgress sx={{marginTop: '4rem'}} size={'120px'}/>;
    if (notFound)
        return <div className={'p-3 text-center mt-2'}>Такого психолога нет.</div>;

    const totalConsultationsOur = (psychologist.total_consultations || 0) + (psychologist.additional_consultations_count || 0);
    const totalConsultationsAll = totalConsultationsOur + (psychologist.other_company_consultations_count || 0);

    return (
        <FRC sx={{...style}} wrap g={2} p={2} maxW={'100vw'} cls={`${className}`}
             ref={!containerRef ? effectiveContainerRef : undefined}>
            {!propId && <PsychologistHead psychologist={psychologist}/>}

            {isSmDown && (
                <FCEC pEvents={false} pos={'fixed'}
                      top={0} left={0} zIndex={20}
                      h={'100%'} w={'100vw'} p={2}>
                    <Button onClick={scrollToSignUp}
                            variant={'contained'} sx={{
                        mx: 'auto',
                        zIndex: 22,
                        width: '100%',
                        opacity: isButtonVisible ? 1 : 0,
                        pointerEvents: isButtonVisible ? 'auto' : 'none',
                        transition: 'opacity 0.3s ease',
                        boxShadow: `0 0 20px 10px ${plt.bg.primary}`
                    }}>
                        Записаться
                    </Button>
                </FCEC>
            )}
            <FC g={2} sx={{maxWidth: {xs: '100%', sm: 550}}}>
                <FR justifyContent={{xs: 'center', md: 'flex-start'}} g={2} wrap>
                    <PsychologistAvatar
                        psychologist={psychologist} size={isSmDown ? '320px' : '12rem'}
                        sx={{mx: isSmDown ? 'auto' : 0}}>
                        <></>
                    </PsychologistAvatar>

                    <FC g={1} maxW={isGt600 ? 340 : 'unset'} alignItems={isGt600 ? 'flex-start' : 'center'}>
                        <FR alignItems={'center'} g={'0.3rem'} wrap
                            justifyContent={isGt600 ? 'flex-start' : 'center'}>
                            <h2 className={'fs-3 m-0'}>
                                {psychologist.user.first_name} {psychologist.user.last_name}
                            </h2>
                            <FavoriteButton psychologist={psychologist}/>
                            {user?.is_staff &&
                                <span className={`fs-6 pt-4px`} style={{color: plt.text.primary50}}>
                                    id:{psychologist.id}
                                </span>}
                            {psychologist.experience_start &&
                                <span className={`px-2 py-1px rounded-5 fw-bold w-min text-nowrap`} style={{
                                    backgroundColor: plt.colors.secondary.light,
                                    color: plt.mode === 'dark' ? '#fff8' : '#fff'
                                }}>
                                    {getYearWord(calculateExperienceYears(psychologist.experience_start))} стажа
                                </span>
                            }
                        </FR>
                        <PsychologistSpecializations psychologist={psychologist}/>
                        <FC g={1}>
                            {psychologist.user.birth_date &&
                                <span>Возраст: {getYearWord(calculateAge(psychologist.user.birth_date))}</span>
                            }
                        </FC>
                    </FC>
                </FR>

                <FC g={1}>
                    <FR justifyContent={{xs: 'center', sm: 'flex-start'}} g={1} wrap>
                        <FC w={'min-content'} rounded={3} px={2} py={1} bg={plt.bg.contrast05}>
                            <span className={'text-nowrap'} style={{color: plt.text.primary60}}>
                                Оценили
                            </span>
                            <span className={'fs-4 fw-bold'} style={{color: plt.colors.secondary.main}}>
                                {psychologist.feedbacks_count}
                            </span>
                        </FC>
                        <FC w={'min-content'} rounded={3} px={2} py={1} bg={plt.bg.contrast05}>
                            <span className={'text-nowrap'} style={{color: plt.text.primary60}}>
                                Всего консультаций
                            </span>
                            <span className={'fs-4 fw-bold'} style={{color: plt.colors.secondary.main}}>
                                {totalConsultationsAll}
                            </span>
                        </FC>
                        <FC w={'min-content'} rounded={3} px={2} py={1} bg={plt.bg.contrast05}>
                            <span className={'text-nowrap'} style={{color: plt.text.primary60}}>
                                Консультаций на Life-Help
                            </span>
                            <span className={'fs-4 fw-bold'} style={{color: plt.colors.secondary.main}}>
                                {totalConsultationsOur}
                            </span>
                        </FC>
                    </FR>
                    <PsychologistAboutTabs psychologist={psychologist}/>
                </FC>
            </FC>

            <FC sx={{maxWidth: {xs: '100%', sm: 550, md: 360}}} w={'100%'}>
                <GuaranteeCard cls={'gap-2 p-3 rounded-3 h-min'}/>
                <FC w={'100%'} cls={'mt-1'} ref={signUpRef}>
                    <CommonOrderOptionsPicker
                        psychologist={psychologist}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        onConfirm={handleSubmit(onSubmit)}
                        isValid={isValid}
                        selectedSlot={selectedSlotWatch}
                        defaultSupervision={false}
                        defaultIsOffline={false}
                        hideSupervision={true}
                        hideOffline={true}
                        defaultConsultationConfigId={defaultConsultationConfigId}
                        defaultDateTimeSlot={defaultDateTimeSlot}
                    />
                </FC>
                {(defaultConsultationConfigId || defaultDateTimeSlot) && (
                    <FCSC mt={1} g={1}>
                        <Button
                            variant={'contained'}
                            color={plt.colors.secondary.light}
                            sx={{opacity: '70%',}}
                            onClick={() => removeSettingsParamsFromURL(navigate)}
                            size={'small'} fullWidth>
                            Изменить параметры
                        </Button>
                    </FCSC>
                )}
            </FC>
            <ConfirmationModal
                open={showModal}
                onClose={() => setShowModal(false)}
                onConfirm={handleUseBalance}
                onCancel={handleProceedToPayment}
            />
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Person",
                    "name": psychologist?.user?.first_name + " " + psychologist?.user?.last_name,
                    "image": psychologist.user.avatar,
                    "url": window.location.href,
                    "jobTitle": "Психолог",
                    "description": psychologist.experience_text
                })}
            </script>
        </FRC>
    );
};

export default PsychologistDetail;